/**
 * Panel administración de organización: usuarios, invitaciones, uso
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { X, Users, Mail, BarChart3, Building2, CreditCard } from 'lucide-react'
import { apiClient } from '@/services/apiClient'
import { useAuthStore } from '@/stores/useAuthStore'
import { hasPermission } from '@/utils/permissions'

interface TenantAdminModalProps {
  isOpen: boolean
  onClose: () => void
}

type Tab = 'users' | 'invite' | 'usage' | 'org'

export function TenantAdminModal({ isOpen, onClose }: TenantAdminModalProps) {
  const user = useAuthStore((s) => s.user)
  const canUsers = hasPermission(user?.role, 'user:list', user?.permissions)
  const canInvite = hasPermission(user?.role, 'user:invite', user?.permissions)
  const canOrg = hasPermission(user?.role, 'tenant:read', user?.permissions)

  const [tab, setTab] = useState<Tab>('users')
  const [users, setUsers] = useState<
    Array<{ id: string; email: string; name: string; role: string; active: boolean }>
  >([])
  const [invites, setInvites] = useState<Array<{ id: string; email: string; role: string; expiresAt: string }>>([])
  const [usage, setUsage] = useState<{
    tenant: Record<string, unknown>
    usage: Record<string, number>
    limits: Record<string, number | null>
  } | null>(null)
  const [orgName, setOrgName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('DESIGNER')
  const [lastToken, setLastToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const loadUsers = useCallback(async () => {
    if (!canUsers) return
    setLoading(true)
    setError(null)
    try {
      const data = await apiClient.get<typeof users>('/api/tenant/users')
      setUsers(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [canUsers])

  const loadInvites = useCallback(async () => {
    if (!canUsers) return
    try {
      const data = await apiClient.get<typeof invites>('/api/tenant/invitations')
      setInvites(data)
    } catch {
      /* ignore */
    }
  }, [canUsers])

  const loadUsage = useCallback(async () => {
    if (!canOrg) return
    setLoading(true)
    try {
      const data = await apiClient.get<typeof usage>('/api/tenant/usage')
      setUsage(data)
      const settings = await apiClient.get<{ name: string }>('/api/tenant/settings')
      setOrgName(settings.name)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [canOrg])

  useEffect(() => {
    if (!isOpen) return
    if (tab === 'users') loadUsers()
    if (tab === 'invite') {
      loadInvites()
      loadUsers()
    }
    if (tab === 'usage' || tab === 'org') loadUsage()
  }, [isOpen, tab, loadUsers, loadInvites, loadUsage])

  if (!isOpen) return null

  const sendInvite = async () => {
    setError(null)
    setLastToken(null)
    try {
      const res = await apiClient.post<{ inviteLinkToken: string }>('/api/tenant/invitations', {
        email: inviteEmail,
        role: inviteRole,
      })
      setLastToken(res.inviteLinkToken)
      setInviteEmail('')
      loadInvites()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const saveOrg = async () => {
    setError(null)
    try {
      await apiClient.patch('/api/tenant/settings', { name: orgName })
      await loadUsage()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const toggleUserActive = async (id: string, active: boolean) => {
    try {
      await apiClient.patch(`/api/tenant/users/${id}`, { active })
      loadUsers()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const changeUserRole = async (id: string, role: string) => {
    try {
      await apiClient.patch(`/api/tenant/users/${id}`, { role })
      loadUsers()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-dark-600">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-600">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Administración de organización
          </h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-1 px-2 pt-2 border-b border-gray-100 dark:border-dark-700">
          {canUsers && (
            <TabBtn active={tab === 'users'} onClick={() => setTab('users')} icon={<Users className="w-4 h-4" />} label="Usuarios" />
          )}
          {canInvite && (
            <TabBtn active={tab === 'invite'} onClick={() => setTab('invite')} icon={<Mail className="w-4 h-4" />} label="Invitar" />
          )}
          {canOrg && (
            <>
              <TabBtn active={tab === 'usage'} onClick={() => setTab('usage')} icon={<BarChart3 className="w-4 h-4" />} label="Uso / plan" />
              <TabBtn active={tab === 'org'} onClick={() => setTab('org')} icon={<Building2 className="w-4 h-4" />} label="Organización" />
            </>
          )}
        </div>

        <div className="p-4 overflow-y-auto flex-1 text-sm">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 p-2.5 bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-100 dark:border-dark-600 leading-relaxed">
            Esta pantalla muestra solo datos de <strong className="text-gray-800 dark:text-gray-200">tu organización</strong>{' '}
            (la del inicio de sesión, o la que un superadmin elige con el slug del mapa). No mezcla usuarios ni redes de otras
            empresas. Para ver el listado global de organizaciones usa <strong>Plataforma</strong>.
          </p>
          {error && <p className="text-red-600 dark:text-red-400 mb-2">{error}</p>}
          {loading && <p className="text-gray-500 mb-2">Cargando…</p>}

          {tab === 'users' && canUsers && (
            <ul className="space-y-2">
              {users.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-dark-700">
                  <span className="font-medium">{u.name}</span>
                  <span className="text-gray-500">{u.email}</span>
                  <select
                    className="text-xs border rounded px-1 dark:bg-dark-800"
                    value={u.role}
                    onChange={(ev) => changeUserRole(u.id, ev.target.value)}
                    disabled={u.id === user?.id}
                  >
                    {['TENANT_ADMIN', 'DESIGNER', 'TECHNICIAN', 'VIEWER'].map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={u.active}
                      onChange={(ev) => toggleUserActive(u.id, ev.target.checked)}
                      disabled={u.id === user?.id}
                    />
                    Activo
                  </label>
                </li>
              ))}
            </ul>
          )}

          {tab === 'invite' && canInvite && (
            <div className="space-y-3">
              <div className="flex flex-col gap-2">
                <input
                  type="email"
                  placeholder="Email"
                  className="border rounded px-3 py-2 dark:bg-dark-700 dark:border-dark-600"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <select
                  className="border rounded px-3 py-2 dark:bg-dark-700 dark:border-dark-600"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  <option value="DESIGNER">Diseñador</option>
                  <option value="TECHNICIAN">Técnico</option>
                  <option value="VIEWER">Solo lectura</option>
                  <option value="TENANT_ADMIN">Admin organización</option>
                </select>
                <button
                  type="button"
                  onClick={sendInvite}
                  className="py-2 rounded-lg bg-primary-600 text-white font-medium"
                >
                  Crear invitación
                </button>
              </div>
              {lastToken && (
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-xs break-all">
                  Token (compartir con el invitado para completar registro en la pantalla de login → Invitación):{' '}
                  <code>{lastToken}</code>
                </div>
              )}
              <p className="text-xs text-gray-500">Invitaciones pendientes:</p>
              <ul className="text-xs space-y-1">
                {invites.map((i) => (
                  <li key={i.id}>
                    {i.email} — {i.role} — exp. {new Date(i.expiresAt).toLocaleDateString()}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tab === 'usage' && usage && (
            <div className="space-y-2">
              <p>
                Plan: <strong>{String(usage.tenant.plan)}</strong> — Estado:{' '}
                <strong>{String(usage.tenant.subscriptionStatus)}</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-2 py-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm"
                  onClick={async () => {
                    setError(null)
                    try {
                      const { url } = await apiClient.post<{ url: string }>(
                        '/api/billing/checkout-session',
                        {}
                      )
                      window.location.href = url
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'No se pudo iniciar el pago')
                    }
                  }}
                >
                  <CreditCard className="w-4 h-4" />
                  Suscribirse a Pro (Stripe)
                </button>
                {user?.tenant?.hasStripeCustomer && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-600 text-sm"
                    onClick={async () => {
                      setError(null)
                      try {
                        const { url } = await apiClient.post<{ url: string }>(
                          '/api/billing/portal-session',
                          {}
                        )
                        window.location.href = url
                      } catch (e) {
                        setError(e instanceof Error ? e.message : 'Portal no disponible')
                      }
                    }}
                  >
                    Portal de facturación
                  </button>
                )}
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-3 py-2 text-sm text-primary-600 underline"
                >
                  Ver planes
                </Link>
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Recurso</th>
                    <th className="text-right">Uso</th>
                    <th className="text-right">Límite</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usage.usage).map(([k, v]) => (
                    <tr key={k} className="border-b border-gray-100 dark:border-dark-700">
                      <td className="py-1">{k}</td>
                      <td className="text-right">{v}</td>
                      <td className="text-right">
                        {usage.limits[k] === null ? '∞' : String(usage.limits[k])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'org' && canOrg && (
            <div className="space-y-2">
              <label className="block text-xs text-gray-500">Nombre de la organización</label>
              <input
                className="w-full border rounded px-3 py-2 dark:bg-dark-700 dark:border-dark-600"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
              <button type="button" onClick={saveOrg} className="px-4 py-2 rounded-lg bg-primary-600 text-white">
                Guardar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TabBtn({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-2 text-sm rounded-t-lg ${
        active ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800' : 'text-gray-600 hover:bg-gray-50 dark:hover:bg-dark-700'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}
